<script setup>
import {onMounted, reactive, ref} from 'vue'
import {State, clone, go, unique} from '@/paks/vu-app'
import {SelectDisplay, getDisplay, updateDisplay} from '@/paks/vu-dash'

const props = defineProps({})

const emit = defineEmits(['input'])

const page = reactive({
    displayName: null,
    display: {},
    displays: [],
    features: [],
    groups: [],
})

const Features = [
    {group: 'Authentication', path: 'auth.forgot', type: 'checkbox', tip: 'Forgot password recovery'},
    {group: 'Authentication', path: 'auth.login', type: 'checkbox', tip: 'Login required to use app'},

    {group: 'Cloud', path: 'cloud.enable', type: 'checkbox', tip: 'Enable cloud-based management'},

    {group: 'Dashboards', path: 'dash.edit', type: 'checkbox', tip: 'Enable user dashboard editing via toolbar widget'},
    {group: 'Dashboards', path: 'dash.database', type: 'checkbox', tip: 'Enable dashboards database access'},
    {group: 'Dashboards', path: 'dash.metrics', type: 'checkbox', tip: 'Enable dashboard metrics'},
    // {group: 'Dashboards', path: 'dash.multiple', type: 'checkbox', tip: 'Support multiple dashboards'},
    // {group: 'Dashboards', path: 'dash.responsive', type: 'checkbox', tip: 'Support responsive dashboards'},

    // {group: 'Demo', path: 'demo.enable', type: 'checkbox', tip: ''},

    // {group: 'Navigation', path: 'nav.navbar', type: 'checkbox', tip: 'Add top level app navigation bar'},

    {group: 'Navigation', path: 'nav.sidebar', type: 'checkbox', tip: 'Add app sidebar navigation'},
    {group: 'Navigation', path: 'nav.sidebarHide', type: 'checkbox', tip: 'Hide sidebar when not in use'},

    {group: 'Navigation', path: 'nav.cookies', type: 'checkbox', tip: 'Display navigation cookies in tabbed pages'},
    {group: 'Navigation', path: 'nav.doc', type: 'checkbox', tip: 'Display documentation link in navbar'},
    {group: 'Navigation', path: 'nav.help', type: 'checkbox', tip: 'Support help panel'},

    {group: 'Navigation', path: 'nav.alerts', type: 'checkbox', tip: 'Display alert notifications in navbar'},
    {group: 'Navigation', path: 'nav.dark', type: 'checkbox', tip: 'Display dark theme option in navbar'},
    {group: 'Navigation', path: 'nav.login', type: 'checkbox', tip: 'Display user login submenu'},
    {group: 'Navigation', path: 'nav.profile', type: 'checkbox', tip: 'Display user account profile link'},
    {group: 'Navigation', path: 'nav.settings', type: 'checkbox', tip: 'Display user account settings link'},
    // {group: 'Navigation', path: 'nav.signposts', type: 'checkbox', tip: ''},

    {group: 'Widgets', path: 'widget.actions', type: 'checkbox', tip: 'Enable widget actions'},
    {group: 'Widgets', path: 'widget.multiCloud', type: 'checkbox', tip: 'Widgets should support multiple clouds'},
]

//  Component refs
const self = ref(null)
const confirm = ref(null)

onMounted(async () => {
    page.displayName = State.app.display.name
    page.displays = State.app.fullDisplay.displays || []
    page.display = getDisplay(page.displayName)

    page.features = mapFeatures(Features)
    page.groups = unique(Features.map((f) => f.group))
})

function changeDisplay(displayName) {
    page.displayName = displayName
    page.display = getDisplay(page.displayName)
    page.views = clone(page.display.views)
}

function mapFeatures(features) {
    features = clone(features)
    for (let item of features) {
        let value = State.app.display.features
        for (let prop of item.path.split('.')) {
            value = value[prop]
            if (!value) break
        }
        item.value = value
    }
    return features
}

async function save() {
    let features = clone(page.display.features)
    for (let item of page.features) {
        let feature = features
        let leaf
        let parts = item.path.split('.')
        for (let prop of parts) {
            leaf = feature
            feature = feature[prop]
        }
        if (leaf) {
            leaf[parts.at(-1)] = item.value
        }
    }
    page.display.features = features
    await updateDisplay(page.display)
    emit('input')
}

function cancel() {
    go({name: 'pages'})
}
</script>

<template>
    <div class="view page feature-edit">
        <SelectDisplay v-if="page.displays.length > 1" v-model="page.displayName" @input="changeDisplay" />

        <vu-form cols="12" help="/doc/ui/view/feature-edit.html" ref="self" :data="page" :save="save">

            <div class="actions">
                <v-btn size="small" color="accent" type="submit" :loading="page.saving">Save</v-btn>
                <v-btn size="small" color="none" @click="cancel">Cancel</v-btn>
            </div>

            <v-card v-for="group of page.groups" class="my-2 vcol"
                :class="State.app.mobile ? 'vcol-12' : 'vcol-6'">
                <v-card-title class="groups">{{ group }}</v-card-title>
                <v-card-text>
                    <div v-for="item of page.features.filter((f) => f.group == group)" class="vrow">
                        <vu-input
                            v-model="item.value"
                            :type="item.type"
                            :cols="6"
                            :label="`${item.tip} (${item.path})`"
                            hide-details 
                            />
                    </div>
                </v-card-text>
            </v-card>
            <vu-confirm ref="confirm" />
        </vu-form>
    </div>
</template>

<style lang="scss">
.feature-edit {
    font-size: 0.9rem;
    margin-bottom: 40px;
    .vu-form {
        width: unset !important;
        margin-top: 16px !important;
        padding: 0 !important;
        column-gap: 12px;
        .v-input,
        .v-label {
            font-size: 0.9rem;
        }
        .v-card {
            background-color: rgb(var(--v-theme-background-lighten-1));
            border: 1px solid rgb(var(--v-theme-accent));
        }
        .v-card-title {
            font-size: 1.4em;
        }
        .groups {
            font-size: 1.2em;
        }
        .v-select-list {
            padding: 0;
            border: solid 1px rgb(var(--v-theme-border-lighten-1));
            border-radius: 0;
        }
        .v-selection-control {
            min-height: unset !important;
        }
        h3 {
            font-weight: normal;
            color: rgb(var(--v-theme-text));
        }
    }
    .actions {
        margin: 0 0 8px 2px !important;
        .v-btn {
            margin-right: 10px;
        }
    }
}
</style>
